// SPDX-License-Identifier: Apache-2.0

import React, { Component, ErrorInfo } from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children?: React.ReactNode;
}

//catches any unexpected errors.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render the message, never the Error object: an object is not a valid
      // React child, so rendering it threw again inside the boundary and the
      // whole tree unmounted — every caught error became a blank page with
      // only the background visible (mainnet, 2026-09-08).
      const err = this.state.error;
      const text = err instanceof Error ? err.message : String(err);
      return (
        <div className={"error-boundary"}>
          <p>Something went wrong: {text}</p>
          <p>Reload the page to continue.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
